import { FC } from 'react';
import { toast, ToastContainer, ToastOptions } from 'react-toastify';
import { Text } from '@connect-x-app/hybrid-ui';
import { ColorName, COLORS } from '@connect-x-app/shared-ui';
import { formatTime, getNow } from '@connect-x-app/shared-utils';

import { Icon } from '../icon';

import 'react-toastify/dist/ReactToastify.css';
import './toastify.scss';

const CloseButton: FC<{
  closeToast?: () => void;
  color?: ColorName;
}> = ({ closeToast, color = COLORS.uniform.$01 }) => (
  <Icon colorName={color} name="closeCross" onClick={closeToast ?? (() => {})} size={2.4} />
);

const ToastMessageContainer: FC<{ color?: ColorName; message: string }> = ({ color = COLORS.yuma.$01, message }) => {
  const formattedTime = formatTime(getNow());
  return (
    <div className="toast-message-container">
      <Text as="span" color={color} size="XS" weight="semiBold">
        {message}
      </Text>
      {formattedTime ? (
        <Text as="span" className="toast-message-time" color={color} size="XXS" weight="semiBold">
          {formattedTime}
        </Text>
      ) : null}
    </div>
  );
};

export const toastSuccess = (message: string, options?: ToastOptions) => {
  return toast.success(<ToastMessageContainer message={message} />, {
    bodyClassName: 'toast-body',
    className: 'toast',
    icon: <Icon name="check" size={2} />,
    ...options,
  });
};

export const toastError = (message: string, options?: ToastOptions) => {
  return toast.error(<ToastMessageContainer message={message} />, {
    bodyClassName: 'toast-body',
    className: 'toast',
    icon: <Icon name="triangleCoreAttention" size={2} />,
    progress: undefined,
    ...options,
  });
};

export const toastWarning = (message: string, options?: ToastOptions) => {
  return toast.warn(<ToastMessageContainer color={COLORS.bogota.$12} message={message} />, {
    bodyClassName: 'toast-body',
    className: 'toast',
    closeButton: <CloseButton color={COLORS.bogota.$12} />,
    icon: <Icon name="exclamationDiamondOutline" size={2} />,
    progress: undefined,
    ...options,
  });
};

export const toastInfo = (message: string, options?: ToastOptions) => {
  return toast.info(<ToastMessageContainer message={message} />, {
    bodyClassName: 'toast-body',
    className: 'toast',
    closeButton: <CloseButton />,
    progress: undefined,
    ...options,
  });
};

export const clearToast = (id: string) => {
  toast.dismiss(id);
};

export const Toastify: FC = () => (
  <ToastContainer closeButton={<CloseButton />} closeOnClick={false} hideProgressBar position="top-right" theme="colored" />
);
